<template>
	<div
		class="relative h-full pt-14 pr-8 pl-6 print:mt-8 print:max-w-300 print:min-w-300 print:p-0!"
		:class="{ 'max-w-300 min-w-300': isPrinting }"
	>
		<div
			v-if="sessionStore.activeSession"
			ref="scrollContainer"
			class="relative flex h-full overflow-y-scroll print:m-0 print:max-w-300 print:min-w-300"
		>
			<div
				class="sticky top-0 flex flex-col justify-between pr-4 pb-4 print:hidden"
			>
				<RadioGroup
					v-model="activePlanIndex"
					class="avatars pr-2"
					as="div"
				>
					<div class="flex flex-col items-center justify-center">
						<RadioGroupOption
							v-for="(plan, index) in sessionStore.activePlans"
							:key="plan.id"
							:value="index"
							class="relative cursor-pointer p-2 focus:outline-hidden"
							as="div"
							@click="handleAvatarClick(index)"
						>
							<PlanAvatar
								:plan="plan"
								large
								:index="index"
								:filled="index === activePlanIndex"
							/>
						</RadioGroupOption>
						<button
							type="button"
							:aria-label="t('memorialGuide.add')"
							@click="showAddParticipant()"
						>
							<PlusCircleIcon
								class="text-accent-400 hover:text-accent-500 mt-1 size-6"
								aria-hidden="true"
							/>
						</button>
					</div>
				</RadioGroup>

				<button
					type="button"
					class="group text-primary-500 hover:text-primary-600 flex border-t border-slate-200 pt-4"
					@click="handlePrint"
				>
					<PrinterIcon
						class="text-primary-500 group-hover:text-primary-600 mr-1 size-6"
						aria-hidden="true"
					/>{{ t('memorialGuide.print') }}
				</button>
			</div>

			<!-- Main Content -->
			<div
				class="w-full"
				data-area="print"
				v-if="activePlan"
			>
				<div class="flex items-center justify-between">
					<h2
						class="w-full pb-4 pl-6 text-3xl capitalize italic print:flex print:items-baseline print:justify-between print:border-b-3 print:border-slate-900 print:text-lg print:font-bold print:not-italic"
					>
						{{
							t(
								'plan.memorialGuideTitle',
								{
									name: `${activePlan.firstName} ${activePlan.lastName}`.trim(),
								},
								activePlan.firstName || activePlan.lastName ? 0 : 1
							)
						}}
						<PrintDate />
					</h2>
					<!-- Delete Plan Button -->
					<button
						v-if="activePlan"
						:disabled="isDeleteDisabled"
						:title="isDeleteDisabled ? t('memorialGuide.atLeastOnePerson') : ''"
						@click="deleteModalOpen = true"
						class="text-secondary-300 hover:text-secondary-400 disabled:text-secondary-300 disabled:hover:text-secondary-300 ml-2 cursor-pointer transition disabled:cursor-not-allowed"
					>
						<TrashIcon class="size-5 print:hidden" />
					</button>
				</div>

				<MemorialGuideForm
					:plan="activePlan"
					:index="activePlanIndex"
				/>
				<div
					v-if="!isPrinting"
					class="flex w-full justify-end py-12"
				>
					<button
						v-if="hasNextSlide"
						type="button"
						@click="onAdvanceButtonClick"
						class="bg-accent-400 text-accent-700 hover:bg-accent-700 flex flex-row items-center gap-2 rounded-lg px-10 py-2 font-sans text-base leading-loose font-bold hover:text-white"
					>
						{{ t('memorialGuide.viewYourFuneralOptions') }}
						<ArrowLongRightIcon class="size-6" />
					</button>
				</div>
			</div>
		</div>
		<div
			v-else
			class="flex h-full items-center justify-center"
		>
			{{ t('memorialGuide.noActiveSession') }}
		</div>
		<DeletePlanModal
			v-if="deleteModalOpen"
			v-model="deleteModalOpen"
			:plan="activePlan"
			:key="activePlan?.id"
			@deleted="handlePlanDeleted"
		/>
		<PrecoaModal v-model:open="showAddParticipantModal">
			<AddParticipantModalContent
				:contacts="unusedContacts"
				@cancel="showAddParticipantModal = false"
				@contact-selected="addParticipant"
			/>
		</PrecoaModal>
	</div>
</template>

<script setup lang="ts">
import Plan from '@/classes/Plan'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import PrintDate from '@/components/common/PrintDate.vue'
import DeletePlanModal from '@/components/memorialGuide/DeletePlanModal.vue'
import MemorialGuideForm from '@/components/memorialGuide/MemorialGuideForm.vue'
import PlanAvatar from '@/components/presentation/PlanAvatar.vue'
import { usePresentationLocale } from '@/i18n'
import { initializePlanOap } from '@/mixins/oapPlanProvider'
import type { MemorialGuideSlideStoryblok } from '@/sbTypes'
import type { Contact } from '@/services/CloudSync/cloudSyncPlanService'
import { getUnusedContacts } from '@/services/contactHelper'
import { usePlanStore } from '@/stores/plan'
import { usePresentationController } from '@/stores/presentationController'
import { useSessionStore } from '@/stores/session'
import AddParticipantModalContent from '@/views/NewSession/AddParticipantModalContent.vue'
import { RadioGroup, RadioGroupOption } from '@headlessui/vue'
import {
	ArrowLongRightIcon,
	PlusCircleIcon,
	PrinterIcon,
	TrashIcon,
} from '@heroicons/vue/24/solid'
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	type PropType,
	ref,
	watch,
} from 'vue'

const { t } = usePresentationLocale()

defineProps({
	blok: Object as PropType<MemorialGuideSlideStoryblok>,
})

const presentationController = usePresentationController()
const sessionStore = useSessionStore()
const planStore = usePlanStore()
const activePlanIndex = ref(0)
const activePlan = ref(sessionStore.activePlans?.[activePlanIndex.value])
const isPrinting = ref(false)
const deleteModalOpen = ref(false)
const showAddParticipantModal = ref(false)
const contactList = ref<Contact[]>([])

const unusedContacts = computed(() => {
	return getUnusedContacts(sessionStore.activePlans, contactList.value ?? [])
})

/**
 * Adds a new participant plan to the active session.
 * If there's no active session, it throws an error.
 * Updates the active plan index to the newly added plan.
 *
 * @throws {Error} Throws an error if there is no active session.
 */
async function showAddParticipant() {
	if (!sessionStore.activeSession) {
		throw new Error('No active session')
	}

	if (unusedContacts.value.length) {
		// open the modal for add participant
		showAddParticipantModal.value = true
	} else {
		await addNewPlan('new')
	}
}

async function addParticipant(contact: Contact | 'new') {
	showAddParticipantModal.value = false
	if (!sessionStore.activeSession) {
		throw new Error('No active session')
	}

	await addNewPlan(contact)
}

const addNewPlan = async (contact: Contact | 'new') => {
	const createFromContact = contact !== 'new'

	let plan: Plan

	if (createFromContact) {
		plan = new Plan({
			...contact,
			leadId: contact?.leadId ?? activePlan.value?.leadId,
			opportunityId: contact?.opportunityId ?? activePlan.value?.opportunityId,
			funeralHomeId: activePlan.value?.funeralHomeId,
			encryptionStatus: 'unencrypted',
		})
	} else {
		plan = new Plan({
			leadId: activePlan.value?.leadId,
			opportunityId: activePlan.value?.opportunityId,
			funeralHomeId: activePlan.value?.funeralHomeId,
			encryptionStatus: 'unencrypted',
		})
	}

	initializePlanOap(plan)

	await planStore.addPlan(plan, true)
	sessionStore.activeSession!.planIds.push(plan.id)
	activePlanIndex.value = sessionStore.activePlans?.length - 1
	activePlan.value = sessionStore.activePlans[activePlanIndex.value]
}

// Disable the delete button if there is only one plan left
const isDeleteDisabled = computed(() => sessionStore.activePlans.length <= 1)

// When a plan is deleted, update the active plan index to be the last remaining plan
function handlePlanDeleted() {
	activePlanIndex.value = sessionStore.activePlans?.length - 1
	activePlan.value = sessionStore.activePlans[activePlanIndex.value]
}

/**
 * Updates the active plan index with the provided index.
 * @param {number} index - The index of the active plan.
 * @return {void}
 */
function handleAvatarClick(index: number) {
	activePlanIndex.value = index
}

const controller = usePresentationController()

/**
 * Computes whether the next slide button should be visible or not.
 * If we are not on the last slide, it should be visible.
 */
const hasNextSlide = computed(() => {
	const swiper = controller.swiper
	if (!swiper) return false

	const currentIndex = controller.activeSlideIndex
	const totalSlides = controller.allSlides?.length ?? 0
	return currentIndex < totalSlides - 1
})

/** Go to the next slide */
function onAdvanceButtonClick() {
	controller.gotoNextSlide()
}

/**
 * Print the Memorial Guide Page
 */
async function handlePrint() {
	// When calling print from code, we have to give
	// the app more time to resize text boxes before printing.
	isPrinting.value = true

	await new Promise((r) => setTimeout(r, 100))

	const event = new Event('triggerautoheight')
	window.dispatchEvent(event)

	await new Promise((r) => setTimeout(r, 100))

	window.print()
}

/**
 * Watches for changes in the activePlanIndex and updates the active plan accordingly.
 * @param {Ref<number>} activePlanIndex - The reactive reference to the active plan index.
 */
watch(activePlanIndex, (newIndex) => {
	activePlan.value = sessionStore.activePlans?.[newIndex]
})

// Keep track of print status so the page can be reformatted and auto-height can reevaluate.
onMounted(async () => {
	contactList.value = await sessionStore.getContactsForActiveSession()
	const beforePrint = async () => {
		isPrinting.value = true
		// Added in an attempt to fix intermitent print misalignment/width issue
		presentationController.swiper?.update()
	}

	const afterPrint = async () => {
		isPrinting.value = false
	}

	window.addEventListener('beforeprint', beforePrint)
	window.addEventListener('afterprint', afterPrint)

	// We have to call this so that Swiper applies width to the style of this
	// and the previous swiper-slide. Without the width, the print is misaligned.
	// Use nextTick to wait for the DOM to update before calling update.
	nextTick(() => {
		presentationController.swiper?.update()
	})

	// Clean up the event listeners
	onBeforeUnmount(() => {
		window.removeEventListener('beforeprint', beforePrint)
		window.removeEventListener('afterprint', afterPrint)
	})
})
</script>

<style scoped>
@import '@/assets/styles/memorialGuidePrint.css';
@media print {
	@page {
		margin: 0.1in;
		width: 8.5in;
		height: 11in;
	}
}
</style>
